/**
 * DropdownMenu — cream-themed wrappers over Radix DropdownMenu. Re-exports the
 * styled parts plus a couple of conveniences (Label, Separator). Compose:
 *
 *   <DropdownMenu>
 *     <DropdownMenuTrigger asChild><IconButton …/></DropdownMenuTrigger>
 *     <DropdownMenuContent>
 *       <DropdownMenuLabel>角色操作</DropdownMenuLabel>
 *       <DropdownMenuItem icon={<Pencil/>}>重命名</DropdownMenuItem>
 *       <DropdownMenuSeparator/>
 *       <DropdownMenuItem danger icon={<Trash2/>}>删除</DropdownMenuItem>
 *     </DropdownMenuContent>
 *   </DropdownMenu>
 */
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import * as RadixMenu from "@radix-ui/react-dropdown-menu";

export const DropdownMenu = RadixMenu.Root;
export const DropdownMenuTrigger = RadixMenu.Trigger;

export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.Content>
>(function DropdownMenuContent({ className, sideOffset = 6, align = "end", ...rest }, ref) {
  return (
    <RadixMenu.Portal>
      <RadixMenu.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        className={["lc-pop", "lc-menu", className].filter(Boolean).join(" ")}
        {...rest}
      />
    </RadixMenu.Portal>
  );
});

export interface DropdownMenuItemProps
  extends ComponentPropsWithoutRef<typeof RadixMenu.Item> {
  /** Optional leading lucide icon. */
  readonly icon?: ReactNode;
  /** Render the destructive (coral) variant. */
  readonly danger?: boolean;
}

export const DropdownMenuItem = forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  function DropdownMenuItem({ icon, danger, className, children, ...rest }, ref) {
    const classes = [
      "lc-menu__item",
      danger ? "lc-menu__item--danger" : null,
      className,
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <RadixMenu.Item ref={ref} className={classes} {...rest}>
        {icon != null ? (
          <span className="lc-menu__item-icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {children}
      </RadixMenu.Item>
    );
  },
);

export const DropdownMenuLabel = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.Label>
>(function DropdownMenuLabel({ className, ...rest }, ref) {
  return (
    <RadixMenu.Label
      ref={ref}
      className={["lc-menu__label", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
});

export const DropdownMenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.Separator>
>(function DropdownMenuSeparator({ className, ...rest }, ref) {
  return (
    <RadixMenu.Separator
      ref={ref}
      className={["lc-menu__separator", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
});
